import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const JobDescriptionLibrary = ({ jobDescriptions = [], onExportJob, onPreviewJob, onDeleteJob }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');

  const languages = [
    { value: '', label: 'All Languages' },
    { value: 'en', label: '🇬🇧 English' },
    { value: 'de', label: '🇩🇪 German' },
    { value: 'es', label: '🇪🇸 Spanish' },
    { value: 'pl', label: '🇵🇱 Polish' },
    { value: 'nl', label: '🇳🇱 Dutch' }
  ];

  const departments = [
    { value: '', label: 'All Departments' },
    { value: 'engineering', label: 'Engineering & Technology' },
    { value: 'product', label: 'Product Management' },
    { value: 'design', label: 'Design & UX' },
    { value: 'marketing', label: 'Marketing & Communications' },
    { value: 'sales', label: 'Sales & Business Development' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance & Accounting' },
    { value: 'operations', label: 'Operations & Supply Chain' },
    { value: 'legal', label: 'Legal & Compliance' },
    { value: 'data', label: 'Data Science & Analytics' }
  ];

  const sortOptions = [
    { value: 'createdAt', label: 'Date Created' },
    { value: 'title', label: 'Job Title' },
    { value: 'department', label: 'Department' },
    { value: 'language', label: 'Language' },
    { value: 'level', label: 'Job Level' }
  ];

  // Filter and sort job descriptions
  const filteredJobs = jobDescriptions
    ?.filter(job => {
      const matchesSearch = !searchTerm || 
        job?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        job?.skills?.some(skill => skill?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
      
      const matchesLanguage = !filterLanguage || job?.languageCode === filterLanguage;
      const matchesDepartment = !filterDepartment || job?.department === filterDepartment;
      
      return matchesSearch && matchesLanguage && matchesDepartment;
    })
    ?.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a?.title?.localeCompare(b?.title);
        case 'department':
          return a?.department?.localeCompare(b?.department);
        case 'language':
          return a?.language?.localeCompare(b?.language);
        case 'level':
          return a?.level?.localeCompare(b?.level);
        case 'createdAt':
        default:
          return new Date(b?.createdAt) - new Date(a?.createdAt);
      }
    });

  const getStatusBadge = (job) => {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
        <Icon name="CheckCircle" size={12} className="mr-1" />
        Generated
      </span>
    );
  };

  const getLanguageFlag = (languageCode) => {
    const flags = {
      en: '🇬🇧',
      de: '🇩🇪',
      es: '🇪🇸',
      pl: '🇵🇱',
      nl: '🇳🇱'
    };
    return flags?.[languageCode] || '🌐';
  };

  const getLevelBadge = (level) => {
    const colors = {
      entry: 'bg-blue-100 text-blue-800',
      mid: 'bg-green-100 text-green-800',
      senior: 'bg-yellow-100 text-yellow-800',
      lead: 'bg-purple-100 text-purple-800',
      executive: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors?.[level] || 'bg-gray-100 text-gray-800'}`}>
        {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Job Description Library</h2>
            <p className="text-sm text-muted-foreground">
              {filteredJobs?.length || 0} of {jobDescriptions?.length || 0} job descriptions
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="Download">
            Export All
          </Button>
          <Button variant="outline" size="sm" iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Input
          placeholder={t('complianceReportsPage.jobs.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          iconName="Search"
        />
        
        <Select
          options={languages}
          value={filterLanguage}
          onChange={setFilterLanguage}
          placeholder={t('complianceReportsPage.jobs.filterByLanguage')}
        />
        
        <Select
          options={departments}
          value={filterDepartment}
          onChange={setFilterDepartment}
          placeholder={t('complianceReportsPage.jobs.filterByDepartment')}
        />
        
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          placeholder={t('complianceReportsPage.jobs.sortBy')}
        />
      </div>

      {/* Job Descriptions List */}
      {filteredJobs?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No Job Descriptions Found</h3>
          <p className="text-muted-foreground mb-4">
            {jobDescriptions?.length === 0 
              ? "Generate your first job description using the Skill Ontology panel above." :"Try adjusting your search criteria or filters."
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs?.map((job) => (
            <div key={job?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-foreground">{job?.title}</h3>
                    {getStatusBadge(job)}
                    {getLevelBadge(job?.level)}
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                    <div className="flex items-center space-x-1">
                      <span>{getLanguageFlag(job?.languageCode)}</span>
                      <span>{job?.language}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Building" size={14} />
                      <span className="capitalize">{job?.department}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(job?.createdAt)?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="DollarSign" size={14} />
                      <span>{job?.payBand}</span>
                    </div>
                  </div>

                  {/* Skills Preview */}
                  <div className="mb-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Brain" size={14} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        Key Skills ({job?.skills?.length || 0})
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {job?.skills?.slice(0, 6)?.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-muted-foreground"
                        >
                          {skill}
                        </span>
                      ))}
                      {job?.skills?.length > 6 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
                          +{job?.skills?.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    onClick={() => onPreviewJob?.(job)}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Download"
                    onClick={() => onExportJob?.(job)}
                  >
                    Export
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onDeleteJob?.(job?.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobDescriptionLibrary;